import { ChangeDetectorRef, Component, HostBinding, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/constraint';
import { IDataSource, IDataSourceItem } from '@se/cp.common';
import {
    EstensaBusWrapperService,
    EstensaReconiceService,
    EstensaSpeechModalService,
    IBusData,
    RadioSettingsService,
} from '@se/platform';
import { Guid, Logger } from '@se/se.ui.html5';
import { MenuConfiguration, MenuEnum, Suggestion, ExtendedSuggestion } from '@se/ui.text.editor';
import { FontInfo } from 'estensa-module';
import { flatten, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { CpTextEditorControl, PropertyType } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { CpReportService } from '../../cp-report.service';
import { cpInitialize } from '../../store/cp.actions';
import { AbstractControlComponent } from '../abstract-control';
import {
    ClinicalDefaultReportRequest,
    ClinicalDefaultReportService,
    SelectedExtendedSuggestionEvent,
} from './clinical-default-report.service';
import { TextEditorConfig } from './text-editor-config';

@Component({
    selector: 'cp-text-editor',
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
})
export class CpTextEditorComponent extends AbstractControlComponent implements OnDestroy {
    @HostBinding('style.height')
    hostHeight = '100%';
    control!: CpTextEditorControl;
    isVisible$!: Observable<boolean>;
    editorConfig$!: Observable<TextEditorConfig>;
    model = '';
    private clinicalDefaultReportRequest?: ClinicalDefaultReportRequest;
    lightLayout: boolean = true;

    suggestions$: BehaviorSubject<Suggestion[]> = new BehaviorSubject<Suggestion[]>([]);

    constructor(
        readonly actions$: Actions,
        readonly clinicalDefaultReportService: ClinicalDefaultReportService,
        readonly dataSourceService: CpDatasourceService,
        readonly estensaBusWrapperService: EstensaBusWrapperService,
        private modelService: CpModelService,
        private estensaReconiceService: EstensaReconiceService,
        private settingsService: RadioSettingsService,
        readonly speechModalService: EstensaSpeechModalService,
        readonly reportService: CpReportService,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        private configuratorService: SeConstraintConfiguratorService,
        private constraintFilterService: SeConstraintApplierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.clinicalDefaultReportRequest) {
            this.clinicalDefaultReportService.unregisterRequest(this.clinicalDefaultReportRequest);
        }
    }

    init$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpInitialize),
                tap(() => this.initializeToolbar()),
            ),
        { dispatch: false },
    );

    onFocus() {
        this.focusin.emit();
    }

    onModelChange(model: string) {
        const changed = this.control.onModelChange(this.modelService, model, !this.isDisabled);
        this.cdr.detectChanges();

        return changed;
    }

    onIsEmptyChange(isEmpty: boolean) {
        this.control.setIsEmpty(isEmpty);

        if (this.entity && this.entity.setPropertyErrorStatus) {
            this.entity.setPropertyErrorStatus(this.control.dataBinding.propertyName, 'isEmpty', true);
        }
        this.formControl?.updateValueAndValidity();
    }

    onVisibilityChange(isVisible: boolean) {
        const dataBinding = this.control.toggleDataBinding;
        if (!dataBinding) {
            return;
        }

        this.modelService.setValue(dataBinding, isVisible);
        this.edited.emit();
        this.formControl?.updateValueAndValidity();
    }

    onStartSpeechReconition(textNode: Array<string>) {
        let id: string = new Guid().toString();
        let speechText: string = textNode[0] ?? '';

        const regex = /<div[^>]*>(.*?)<\/div>/gs;
        const matches = [...speechText.matchAll(regex)];
        speechText = matches.map((match) => match[1]).join('');

        const fontInfo: FontInfo = {
            size: textNode[2]?.split('px')[0],
            family: textNode[1],
        };

        this.speechModalService.openSpeechWindow(id, speechText, fontInfo);
        this.estensaReconiceService.setFont(fontInfo);
        this.speechModalService.setSpeechSkin().then(() => {
            this.configToolbarRefertatore();
            this.speechModalService.setThemeAndColors();
            this.speechModalService.getReconice().Dycendo.StartDictation();
        });
        this.changeTextOnCloseEvent(id);
    }

    configToolbarRefertatore() {
        let textEditorToolbarButtonsEnum = this.speechModalService.getReconice().TextEditorToolbarButtonsEnum;
        this.editorConfig$.subscribe((config: TextEditorConfig) => {
            let buttons: Array<string> = [];
            config.menu.forEach((configuration: MenuConfiguration, menu: MenuEnum) => {
                switch (menu) {
                    case MenuEnum.TEXT_ALIGNMENT:
                        if (!!configuration.visible) {
                            buttons.push(
                                textEditorToolbarButtonsEnum.ALIGN_LEFT,
                                textEditorToolbarButtonsEnum.ALIGN_RIGHT,
                                textEditorToolbarButtonsEnum.ALIGN_CENTER,
                                textEditorToolbarButtonsEnum.ALIGN_JUSTIFY,
                            );
                        }
                        break;
                    case MenuEnum.TEXT_FONT_WEIGHT:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.BOLD);
                        }
                        break;
                    case MenuEnum.TEXT_FONT_STYLE:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.ITALIC);
                        }
                        break;
                    case MenuEnum.TEXT_DECORATION:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.UNDERLINE);
                        }
                        break;
                    case MenuEnum.UNORDERED_LIST:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.BULLET_LIST);
                        }
                        break;
                    case MenuEnum.ORDERED_LIST:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.NUMBERED_LIST);
                        }
                        break;
                    case MenuEnum.NEW:
                    case MenuEnum.UNDO:
                    case MenuEnum.REDO:
                    case MenuEnum.SUGGESTIONS:
                    case MenuEnum.MICROPHONE:
                    default:
                        this.estensaReconiceService.setDefaultVisibleButton();
                        break;
                }
            });
            this.estensaReconiceService.setTextEditorButtonVisibility(uniq(buttons));
        });
    }

    onSelectedSuggestion(extendedSuggestion: ExtendedSuggestion) {
        if (!this.control.defaultText) {
            throw new Error('Configurazione DefaultText incompleta impossibile Selezionare una suggestion');
        }
        this.clinicalDefaultReportService.selectSuggestion(
            extendedSuggestion,
            this.control.defaultText.defaultTextName,
        );
    }

    changeTextOnCloseEvent(speechId: string) {
        if (!this.isDisabled && this.canShowSpeechButton()) {
            this.estensaBusWrapperService.busDataFired
                .pipe(
                    filter((speechWindow: IBusData) => speechWindow.data?.id === speechId),
                    take(1),
                )
                .subscribe((busData: IBusData) => {
                    if (busData.messageType === 'ESTENSA_TEXT_TO_EDITOR') {
                        this.onModelChange(busData.data.text);
                        this.updateFormControlValue(busData.data.text);
                    }
                });
        }
    }

    protected init() {
        this.control.getEntityObservable(this.modelService).subscribe(this._entitySubject);

        this.formControl = this.makeControl();

        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                if (model === this.model) {
                    return;
                }
                this.logger && this.logger.trace(`Model update ${this.control.name} ${model}`);
                this.updateFormControlValue(model);
            });

        this.isVisible$ = this.control.getVisibilityObservable(this.modelService);

        this.initializeToolbar();

        if (this.control.defaultTextEnabled) {
            this.appendSuggestionToGroupTextEditors();
        }
    }

    private appendSuggestionToGroupTextEditors() {
        if (!this.control.defaultText) {
            throw new Error('Configurazione DefaultText incompleta impossibile Selezionare una suggestion');
        }
        if (!this.clinicalDefaultReportRequest) return;
        this.clinicalDefaultReportService
            .getSelectedSuggestion(this.clinicalDefaultReportRequest, this.control.defaultText.defaultTextName)
            .subscribe((event: SelectedExtendedSuggestionEvent) => {
                if (event.suggestion.policy) {
                    this.appendSuggestionToEntityContent(event.suggestion);
                } else {
                    this.replaceSuggestionToEntityContent(event.suggestion);
                }
            });
    }

    private appendSuggestionToEntityContent(extendedSuggestion: ExtendedSuggestion) {
        const currentValue = this.modelService.getValue(this.control.dataBinding);
        const newValue =
            currentValue + extendedSuggestion.suggestion.Sentence[this.control.defaultText?.defaultTextName ?? ''];
        this.onModelChange(newValue);
    }

    private replaceSuggestionToEntityContent(extendedSuggestion: ExtendedSuggestion) {
        //const _currentValue = this.modelService.getValue(this.control.dataBinding);
        const newValue = '' + extendedSuggestion.suggestion.Sentence[this.control.defaultText?.defaultTextName ?? ''];
        this.onModelChange(newValue);
    }

    private initializeToolbar() {
        this.clinicalDefaultReportRequest = this.getClinicalDefaultReportRequest();
        this.editorConfig$ = combineLatest({
            height: this.control.getHeightPxObservable(this.modelService),
            suggestions: this.suggestions$, // this.control.defaultTextEnabled ? this.clinicalDefaultReportService.getSuggestions(this.clinicalDefaultReportRequest) : of([])
        }).pipe(
            map((heightAndSuggestions) => {
                return {
                    height: heightAndSuggestions.height,
                    isToggleEnabled: this.control.isToggleEnabled,
                    menu: this.control
                        .configureToolbar()
                        .set(MenuEnum.MICROPHONE, { visible: this.canShowSpeechButton() }),
                    suggestions: heightAndSuggestions.suggestions,
                    title: this.control.getTitle(),
                };
            }),
        );
        this.retrieveSuggestions();
    }

    private retrieveSuggestions(): void {
        if (this.control.defaultTextEnabled) {
            this.clinicalDefaultReportService
                .getSuggestions(this.clinicalDefaultReportRequest)
                ?.pipe(tap((items) => this.setLayoutType(items)))
                .subscribe((initialSuggestions) => {
                    this.suggestions$.next(initialSuggestions);
                });
        } else if (this.control.suggestions) {
            const initialSuggestions = this.initializeSuggestionsFromDataSource();
            this.suggestions$.next(initialSuggestions);
            const constraints = uniq(
                flatten(
                    this.configuratorService.constraintSources
                        .getSourcesByListName(this.control.suggestions)
                        .map((s) => s.constraints),
                ),
            );

            const dataBindingContext = 'dataBinding' in this.control ? this.control.dataBinding.toString() : undefined;

            for (const c of constraints) {
                this.constraintNotifier
                    .register(c.id, dataBindingContext)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((_) => this.handleFilterNotification(c.id));
            }
        }
    }

    protected handleFilterNotification(constraintId: string) {
        this.entity?.select();
        const filteredItems = this.constraintFilterService.applyFilter(constraintId) as IDataSource;
        const filteredSuggestions = this.transformDataSourceToSuggestions(filteredItems);
        this.suggestions$.next(filteredSuggestions);
    }

    private setLayoutType(suggestions: Array<Suggestion>) {
        if (!!suggestions && suggestions.length > 0) {
            this.lightLayout = Object.keys(suggestions[0].Sentence).length === 1;
        }
    }

    private initializeSuggestionsFromDataSource(): Suggestion[] {
        this.lightLayout = true;
        const suggestionByService: IDataSource =
            (this.control.suggestions ? this.dataSourceService.getById(this.control.suggestions) : undefined) ?? [];
        return this.transformDataSourceToSuggestions(suggestionByService);
    }

    private transformDataSourceToSuggestions(dataSource: IDataSource): Array<Suggestion> {
        let idCount: number = 0;
        return dataSource.map((item: IDataSourceItem) => {
            let suggestion: Suggestion = {
                id: idCount++,
                Description: item.Description,
                Sentence: { text: item['Sentence' as keyof IDataSourceItem] as string },
                IsNormality: !!item['IsNormality' as keyof IDataSourceItem],
                code: item['code' as keyof IDataSourceItem] as string,
                newLineAfter: true,
            };
            return suggestion;
        });
    }

    private getClinicalDefaultReportRequest(): ClinicalDefaultReportRequest | undefined {
        if (!this.control.defaultTextEnabled || !this.control.defaultText) {
            return undefined;
        }
        return {
            groupName: this.control.defaultText.defaultTextGroupName,
            idExaminations: this.reportService.examinationIds ?? [],
            idReferringPhysician: this.reportService.referringPhysicianId,
        };
    }

    private canShowSpeechButton(): boolean {
        return (
            this.settingsService.radioSettings.enableSpeechRecognition &&
            this.estensaReconiceService.isRecomedConnectedAndLoggedOn()
        );
    }

    private makeControl() {
        let validators = [
            // Non aggiungere il required, se ne occupa il validatore custom.
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, false),
            ...this.control.getValidators(this.modelService),
        ];
        return new FormControl(this.control.getInitialValue(this.modelService), validators);
    }

    private updateFormControlValue(model: PropertyType | undefined) {
        if (!this.formControl) {
            return;
        }
        const formControlValue = this.formControl.value;
        // Non aggiornare il campo se i valori sono uguali.
        if (!model && !model === !formControlValue) {
            return;
        }
        this.formControl.setValue(model);
    }
}
